import React, { HTMLAttributes, useEffect, useState } from "react";
import {
  downloadPayReceipts,
  getLast7DayReceiptList,
} from "../../../apis/acePayRecord";
import RefreshIcon from "../../../assets/icons/refetch.png";
import BaseButton from "../../../components/atom/baseButton";
import BaseDarkButton from "../../../components/atom/baseDarkButton";
import BaseTitle from "../../../components/atom/baseTitle";
import useReceiptStore from "../../../store/receipt";
import ReceiptParkingCard from "../parkingCard";

import { Swiper, SwiperSlide } from "swiper/react";

import { EffectCoverflow, Navigation, Pagination } from "swiper/modules";

import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import type { IGetReceiptRecordResponse } from "../../../apis/acePayRecord/type";
import { combineBase64ToPdf } from "../../../helper/convertor";
import { customShowToast } from "../../../helper/toast";
import useGeneralStore from "../../../store/general";

interface IReceiptRecords {
  goNext: () => void;
  goStep?: (step: number) => void;
  goHomeAndRemoveQueryParam: () => void;
}

type TReceiptRecordsProps = IReceiptRecords & HTMLAttributes<HTMLDivElement>;

const ReceiptRecords: React.FC<TReceiptRecordsProps> = (props) => {
  const { goNext, goStep, goHomeAndRemoveQueryParam, ...otherProps } = props;
  const [receiptRecords, setReceiptRecords] = useState<
    IGetReceiptRecordResponse | null | undefined
  >(undefined);

  const navigate = useNavigate();

  const setGlobalLoadingState = useGeneralStore((state) => state.setIsLoading);

  const { receiptFormData, receiptParkingList, selectedId, clearOtp } =
    useReceiptStore((state) => state);

  const currentParkingInfo = receiptParkingList.find(
    (parking) => parking.parking_id === selectedId
  );

  const fetchReceiptRecord = async () => {
    try {
      if (receiptFormData && currentParkingInfo) {
        setReceiptRecords(null); // Reset records to null while fetching
        const receiptRecord = await getLast7DayReceiptList(
          currentParkingInfo.token,
          receiptFormData?.mainCarPlate,
          receiptFormData?.otp,
          receiptFormData?.phone
        );
        if (receiptRecord.code === 400) {
          clearOtp();
          customShowToast("驗證碼已過期,請重新驗證", "error");
          goHomeAndRemoveQueryParam();
          return;
        }
        setReceiptRecords(receiptRecord);
      }
    } catch (error) {
      setReceiptRecords(undefined);
    }
  };

  const downloadSingleReceipt = async (pay_ids: string[]) => {
    try {
      if (receiptFormData && currentParkingInfo) {
        setGlobalLoadingState(true);
        const receiptResponse = await downloadPayReceipts(
          currentParkingInfo?.token,
          receiptFormData?.mainCarPlate,
          pay_ids
        );
        if (receiptResponse) {
          combineBase64ToPdf(
            receiptResponse.data,
            receiptResponse.filename ?? "receipt"
          );

          customShowToast(`已生成收據`, "success");
        }
        setGlobalLoadingState(false);
      }
    } catch (error) {
      console.error("Error downloading receipt:", error);
      customShowToast("下載失敗，請稍後再試", "error");
      setGlobalLoadingState(false);
    }
  };

  useEffect(() => {
    fetchReceiptRecord();
  }, []);

  return (
    <div className="flex flex-1 flex-col gap-4 py-4" {...otherProps}>
      <BaseTitle
        text="最近7次的出入記錄"
        subTitle="Entry and Exit Records for the Last 7 Times"
        textColor="text-white"
      />

      <ReceiptParkingCard />

      <button
        className="flex flex-row items-center justify-end"
        onClick={fetchReceiptRecord}
      >
        <div className="text-white text-sm">刷新</div>
        <img src={RefreshIcon} width={14} height={14} />
      </button>

      <div className="text-xl text-center font-bold">
        查詢車牌碼號: {receiptFormData?.mainCarPlate}
      </div>

      {receiptRecords === null && (
        <div className="skeleton h-[260px] w-full"></div>
      )}

      {receiptRecords && receiptRecords.data.length === 0 && (
        <div className="text-white text-center text-sm my-4">
          沒有出入記錄
          <br />
          No Entry and Exit Records
        </div>
      )}

      {receiptRecords && receiptRecords?.data.length > 0 && (
        <Swiper
          centeredSlides={true}
          slidesPerView={1}
          modules={[Pagination, Navigation, EffectCoverflow]}
          effect={"coverflow"}
          pagination={{
            clickable: true, // Enable pagination clicks
          }}
          className="relative h-[290px] w-full pb-8"
          //   navigation
        >
          {receiptRecords?.data.map((record) => {
            return (
              <SwiperSlide
                className="flex flex-row rounded-xl overflow-hidden bg-[#eeeeee] text-black text-sm "
                key={record.PayID}
              >
                {/* Left column with consistent yellow background */}
                <div className="bg-mainYellow justify-between text-xs gap-2 font-bold flex flex-col p-2 w-[110px]">
                  <div className="h-[50px] flex items-center">
                    入閘時間
                    <br />
                    Entry Time
                  </div>
                  <div className="h-[50px] flex items-center">
                    出閘時間
                    <br />
                    Exit Time
                  </div>
                  <div className="h-[50px] flex items-center">
                    位置
                    <br />
                    Location
                  </div>
                  <div className="h-[50px] flex items-center">
                    收費
                    <br />
                    Price
                  </div>
                  <div className="h-[50px] flex items-center">
                    下載
                    <br />
                    Download
                  </div>
                </div>

                {/* Right column content */}
                <div className="flex flex-col justify-between gap-2 p-2 text-left font-bold">
                  <div className="h-[50px] flex items-center">
                    {dayjs(record.EntryTime).isValid()
                      ? dayjs(record.EntryTime).utc().format("YYYY-MM-DD HH:mm")
                      : "-"}
                  </div>
                  <div className="h-[50px] flex items-center">
                    {dayjs(record.ExitTime).isValid()
                      ? dayjs(record.ExitTime).utc().format("YYYY-MM-DD HH:mm")
                      : "-"}
                  </div>
                  <div className="h-[50px] flex items-center">
                    {receiptRecords.parking.location}
                    <br />
                    {receiptRecords.parking.location_en}
                  </div>
                  <div className="h-[50px] flex items-center">
                    HKD${record.Amount}
                  </div>
                  <div
                    className="h-[50px] flex items-center cursor-pointer"
                    onClick={() =>
                      downloadSingleReceipt([record.PayID.toString()])
                    }
                  >
                    <img
                      src={require("../../../assets/icons/direct_download.png")}
                      alt="download"
                    />
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      )}

      <div className="flex flex-col gap-4 my-3 justify-center items-center">
        <BaseButton
          disabled={receiptRecords?.data.length === 0}
          onClick={() => {
            if (receiptRecords?.data.length === 0) {
              customShowToast("沒有可下載的收據", "info");
              return;
            }
            const payIds = receiptRecords?.data.map((record) =>
              record.PayID.toString()
            );
            downloadSingleReceipt(payIds!);
          }}
          minWidth="w-full"
          disabledStyle="disabled:bg-mainYellow disabled:bg-opacity-60"
        >
          下載全部 Download All
        </BaseButton>

        <BaseDarkButton onClick={goHomeAndRemoveQueryParam}>
          其他停車場 Other Parking
        </BaseDarkButton>
      </div>
    </div>
  );
};

export default ReceiptRecords;
